import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { pasangTurnamenTunggal } from './utils/turnamenTunggal';
import store from './store';
import i18n from './locales';
import motionDirectives from './directives/reveal';
import { initTheme } from './composables/useTheme';
import { setUnauthorizedHandler } from './services/http';

/*
 * Memasang token dan penanganan 401 pada instans axios BAWAAN.
 * Wajib diimpor sebelum komponen mana pun memanggil API — 49 berkas masih
 * memakai `import axios from 'axios'` langsung, dan tanpa berkas ini
 * panggilan mereka dikirim tanpa token.
 */
import axiosGlobal from './services/axios-global';

import { createMetaManager } from 'vue-meta';
import Toast from 'vue-toastification';

// Bootstrap dimuat lebih dulu supaya berkas tema kita bisa menimpanya.
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'vue-toastification/dist/index.css';

/*
 * Urutan berkas gaya penting:
 *   theme.css  -> mendefinisikan seluruh token warna & bentuk
 *   motion.css -> memakai token tersebut untuk kurva dan animasi
 *   global.css -> menyesuaikan kelas warisan agar mengikuti token
 *   admin.css  -> gaya khusus panel admin
 *   jadwal.css -> gaya bersama untuk kelima halaman jadwal
 */
import './assets/css/theme.css';
import './assets/css/motion.css';
import './assets/css/global.css';
import './assets/css/admin.css';
import './assets/css/jadwal.css';

/*
 * Tema diterapkan sebelum aplikasi dipasang. Kalau ditunda sampai komponen
 * pertama dirender, pengguna tema gelap akan melihat kilatan putih sesaat.
 */
initTheme();

// Pengalihan saat sesi habis berlaku untuk KEDUA instans axios.
if (typeof axiosGlobal.setUnauthorizedHandler === 'function') {
  axiosGlobal.setUnauthorizedHandler(() => {
    if (router.currentRoute.value.path !== '/admin/login') {
      router.push('/admin/login');
    }
  });
}

const app = createApp(App);

app.use(store);
app.use(router);
app.use(i18n);
app.use(createMetaManager());
app.use(motionDirectives);

app.use(Toast, {
  position: 'top-right',
  timeout: 4000,
  closeOnClick: true,
  pauseOnHover: true,
  hideProgressBar: false,
  maxToasts: 3,
  newestOnTop: true,
  /*
   * Pelapor galat global dan blok catch di halaman sering menampilkan pesan
   * yang sama untuk satu kegagalan. Pesan yang persis sama dan masih tampil
   * tidak dimunculkan dua kali.
   */
  filterBeforeCreate: (baru, yangTampil) =>
    (yangTampil.some((t) => t.content === baru.content && t.type === baru.type) ? false : baru),
});

/*
 * Ketika API membalas 401, sesi sudah dibersihkan oleh interceptor.
 * Pengguna diarahkan ke halaman masuk, kecuali memang sudah berada di sana
 * supaya tidak terjadi pengalihan berulang.
 */
setUnauthorizedHandler(() => {
  store.dispatch('logout');
  if (router.currentRoute.value.path !== '/admin/login') {
    router.push({
      path: '/admin/login',
      query: { redirect: router.currentRoute.value.fullPath },
    });
  }
});

/*
 * Menentukan judul tab peramban sederhana tanpa strip panjang.
 */
const routeTitleMap = {
  '/': 'Arena Sport Digital',
  '/admin': 'Dashboard',
  '/admin/dashboard': 'Dashboard',
  '/admin/turnamen-saya': 'Turnamen',
  '/admin/tournament': 'Turnamen',
  '/admin/contingent': 'Data Kontingen',
  '/admin/team-members': 'Data Atlet',
  '/admin/team-members/import': 'Impor Atlet',
  '/admin/id-card': 'ID Card',
  '/admin/perkiraan-medali': 'Perkiraan Medali',
  '/admin/payment': 'Tagihan',
  '/admin/payment/buat': 'Buat Tagihan',
  '/admin/ketersediaan-kelas': 'Ketersediaan Kelas',
  '/admin/classes': 'Kelas Tanding',
  '/admin/drawing': 'Undian & Pool',
  '/admin/match-chart': 'Bagan Tanding',
  '/admin/seni-match': 'Bagan Seni',
  '/admin/tournament-schedule': 'Jadwal Tanding',
  '/admin/tournament-schedule-seni': 'Jadwal Seni',
  '/admin/tournament-category': 'Kategori Kejuaraan',
  '/admin/tournament-age-category': 'Kategori Usia',
  '/admin/jenis-tarif': 'Pengaturan Tarif',
  '/admin/tournament-arena': 'Gelanggang',
  '/admin/navigation': 'Menu Navigasi',
  '/admin/sliders': 'Slider',
  '/admin/login': 'Masuk',
  '/registration': 'Pendaftaran Kontingen',
  '/pendaftaran': 'Pendaftaran Kontingen',
  '/jadwal': 'Jadwal Pertandingan',
  '/schedule': 'Jadwal Pertandingan',
  '/about': 'Tentang Kami',
  '/privacy': 'Kebijakan Privasi',
};

/*
 * Kembali ke puncak halaman dan perbarui judul tab setiap kali berpindah rute.
 */
router.afterEach((to, from) => {
  if (to.path !== from.path) {
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }

  let title = routeTitleMap[to.path];
  if (!title) {
    for (const [key, val] of Object.entries(routeTitleMap)) {
      if (key !== '/' && to.path.startsWith(key)) {
        title = val;
        break;
      }
    }
  }
  document.title = title || 'ARSPO';
});

/*
|------------------------------------------------------------------------
| Pemulihan otomatis setelah situs diperbarui
|------------------------------------------------------------------------
|
| MASALAH YANG DITANGANI
| Nama berkas hasil build memuat hash — app.1b478776.js, lalu app.b53b353e.js
| pada build berikutnya. Berkas lama IKUT TERHAPUS saat pemasangan.
|
| Peramban yang masih menyimpan index.html versi lama akan meminta berkas
| yang sudah tidak ada. Karena aturan .htaccess mengarahkan semua alamat
| tak dikenal ke index.html, permintaan itu tidak dijawab 404 melainkan
| dijawab HALAMAN HTML. Peramban mencoba menjalankannya sebagai JavaScript,
| gagal di karakter pertama, dan yang terlihat pengguna adalah HALAMAN
| KOSONG — tanpa pesan galat apa pun.
|
| Ponsel paling sering terkena karena jarang dimuat-ulang paksa; di komputer
| gejalanya hilang sendiri setelah Ctrl+Shift+R, yang membuat masalahnya
| tampak seolah tidak ada.
|
| PENANGANAN
| Muat ulang sekali secara diam-diam. Penanda disimpan di sessionStorage
| supaya kalau galatnya ternyata BUKAN karena berkas basi, halaman tidak
| memuat ulang terus-menerus dalam lingkaran tak berujung.
*/
const PENANDA_MUAT_ULANG = 'arspo:sudah-muat-ulang';

function pulihkanDariBerkasBasi(pesan) {
  const teks = String(pesan || '');
  const berkasBasi =
    /Loading (CSS )?chunk|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|Error loading dynamically imported module|Unable to preload CSS|expected expression, got '<'|Unexpected token '<'/i.test(
      teks
    );

  if (!berkasBasi) return false;

  try {
    const sudahMuatUlang = sessionStorage.getItem(PENANDA_MUAT_ULANG);
    if (!sudahMuatUlang) {
      sessionStorage.setItem(PENANDA_MUAT_ULANG, '1');
      window.location.reload();
      return true;
    } else {
      // Jika sudah sekali reload biasa tapi masih error berkas basi (cache browser keras),
      // paksa ambil berkas HTML baru dengan parameter waktu agar cache terlewatkan.
      sessionStorage.removeItem(PENANDA_MUAT_ULANG);
      window.location.href = window.location.pathname + window.location.search + (window.location.search ? '&' : '?') + '_v=' + Date.now();
      return true;
    }
  } catch (e) {
    window.location.reload();
    return true;
  }
}

app.config.errorHandler = (err, instance, info) => {
  console.error('[Vue Global Error]', err, info);
  pulihkanDariBerkasBasi(err?.message);
};

window.addEventListener('error', (event) => {
  pulihkanDariBerkasBasi(event.message || event.error?.message);
});

window.addEventListener('unhandledrejection', (event) => {
  pulihkanDariBerkasBasi(event.reason?.message || event.reason);
});

router.onError((error) => {
  pulihkanDariBerkasBasi(error?.message);
});

// Navigasi yang berhasil berarti berkasnya sudah segar — penanda dibersihkan
// supaya pemulihan ini tetap tersedia untuk pembaruan berikutnya.
router.isReady().then(() => {
  try {
    sessionStorage.removeItem(PENANDA_MUAT_ULANG);
  } catch (e) {
    // Diabaikan.
  }
});

// Pendaftaran Service Worker untuk PWA & Caching Offline (Kartu Atlet & Aset)
if ('serviceWorker' in navigator && (window.location.protocol === 'https:' || window.location.hostname === 'localhost')) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[PWA] Service Worker gagal didaftarkan:', err);
    });
  });
}

app.mount('#app');

// Admin event & bendahara: pilihan turnamen otomatis terisi dan disembunyikan.
pasangTurnamenTunggal();
